import React, { forwardRef, useState, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';

import Icons from 'assets/icons';

interface InputErrorsNames {
  message?: string;
  [key: string]: unknown;
}

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'type' | 'placeholder'> {
  customClass?: string;
  label?: string;
  labelFor?: string;
  errorsNames?: InputErrorsNames;
  helperText?: string;
  domainText?: string;
  type?: string;
  placeholder?: string;
  disabledClassName?: string;
  inputCustomClass?: string;
  inputWrapperCustomClass?: string;
  labelInTop?: boolean;
  required?: boolean;
  maxLength?: number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  endAdornment?: React.ReactNode;
  startAdornment?: React.ReactNode;
  'data-testid'?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      customClass = '',
      label = '',
      labelFor = '',
      errorsNames = {},
      helperText = '',
      domainText = '',
      type = 'text',
      placeholder = 'Type...',
      disabled,
      disabledClassName = 'bg-gray-100',
      inputCustomClass = '',
      inputWrapperCustomClass = '',
      labelInTop = false,
      required,
      maxLength,
      onChange,
      endAdornment = null,
      startAdornment = null,
      'data-testid': dataTestId = 'input-field',
      ...rest
    },
    ref
  ) => {
    const [charCount, setCharCount] = useState(0);
    const isValid = Object.keys(errorsNames).length === 0;

    useEffect(() => {
      if (!maxLength) return;

      if (!rest.value) {
        setCharCount(0);
      } else {
        setCharCount(String(rest.value).length);
      }
    }, [rest.value, maxLength]);

    const invalidBorder = !isValid;

    const standaloneFieldClass = twMerge(
      'border border-solid rounded-md p-2.5 transition focus:outline-none hover:border-navy-500 focus:border-navy-700 disabled:hover:border-navy-200',
      invalidBorder && 'border-critical-600 focus:border-critical-600 hover:border-critical-600',
      isValid && 'border-navy-200',
      inputCustomClass,
    );

    const compositeShellClass = twMerge(
      'flex w-full min-w-0 items-stretch overflow-hidden rounded-md border border-solid transition hover:border-navy-500 focus-within:border-navy-700 disabled:hover:border-navy-200',
      invalidBorder && 'border-critical-600 focus-within:border-critical-600 hover:border-critical-600',
      isValid && 'border-navy-200',
    );

    const compositeInputClass = twMerge(
      'min-w-0 flex-1 border-0 bg-transparent py-2.5 text-left text-gray-700 shadow-none outline-none focus:outline-none focus:ring-0 disabled:cursor-not-allowed',
      startAdornment ? 'pl-1 pr-1' : 'pl-2.5 pr-1',
      inputCustomClass,
    );

    const labelStyles = twMerge(
      'font-family font-semibold text-sm h-6',
      !labelInTop && 'w-32 mr-14 mb-5',
      labelInTop && 'flex mb-[0.25rem] mt-3',
      customClass,
    );

    const wrapperClass = twMerge(
      inputWrapperCustomClass,
      disabled && disabledClassName,
      disabled && 'rounded-md',
    );

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
      if (!rest.value && maxLength && !disabled) {
        setCharCount(e.target.value.length); // manually count characters if value is not passed
      }

      if (onChange) {
        onChange(e);
      }
    };

    const errorId = !isValid && !disabled ? `${labelFor}-error` : undefined;

    const describedBy =
      [errorId, rest['aria-describedby']].filter(Boolean).join(' ') || undefined;

    const inputProps = {
      id: labelFor,
      ref,
      type,
      placeholder,
      disabled,
      maxLength,
      'data-testid': dataTestId,
      onChange: handleInputChange,
      ...rest,
      'aria-required':
        required && !disabled ? true : rest['aria-required'] ?? undefined,
      'aria-invalid': !isValid ? true : rest['aria-invalid'] ?? undefined,
      'aria-describedby': describedBy,
    };

    const renderAdornmentSlot = (side: 'start' | 'end', content: React.ReactNode): React.JSX.Element | null => {
      if (!content) {
        return null;
      }

      return (
        <div
          className={twMerge(
            'flex shrink-0 select-none items-center text-sm text-gray-500',
            side === 'start' ? 'pl-2.5 pr-0.5' : 'pl-0.5 pr-2.5',
          )}
          data-testid={`${dataTestId}-${side}-adornment`}
        >
          {content}
        </div>
      );
    };

    const fieldControl =
      startAdornment || endAdornment ? (
        <div className={compositeShellClass}>
          {renderAdornmentSlot('start', startAdornment)}
          <input {...inputProps} className={compositeInputClass} />
          {renderAdornmentSlot('end', endAdornment)}
        </div>
      ) : (
        <input {...inputProps} className={standaloneFieldClass} />
      );

    return (
      <div className="flex flex-col w-full">
        <div
          className={twMerge(
            'flex',
            labelInTop ? 'flex-col items-start' : 'items-center',
          )}
        >
          {label && (
            <label htmlFor={labelFor} className={labelStyles}>
              {label}
              {required && !disabled ? (
                <Icons.RequiredIndicator
                  customClass="w-[7px] h-[7px] ml-1 mt-1"
                  aria-hidden="true"
                  data-testid={`${dataTestId}-required-indicator`}
                />
              ) : null}
            </label>
          )}
          <div className={twMerge('flex flex-col w-full', wrapperClass)}>
            {fieldControl}
            {helperText && !disabled && isValid && (
              <span
                className="font-sm pt-1.5 bg-white text-gray-700"
                data-testid={`${dataTestId}-helper-text`}
              >
                {helperText} {domainText}
              </span>
            )}
            {!isValid && !disabled && (
              <span
                id={`${labelFor}-error`}
                role="status"
                className="font-sm pt-1 text-critical-600 bg-white"
                data-testid={`${dataTestId}-error-message`}
              >
                {errorsNames.message}
              </span>
            )}
            {!disabled && maxLength && (
              <span
                className="mt-1 text-sm"
                data-testid={`${dataTestId}-character-count`}
              >
                {charCount} of {maxLength} characters used
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }
);

export default Input;
